import React, { useState, useCallback } from "react";
import { Line } from "react-chartjs-2";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  FormGroup,
  Typography,
} from "@material-ui/core";

import makeStyles from "@material-ui/core/styles/makeStyles";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import SearchIcon from "@material-ui/icons/Search";

import FromToDateField from "components/_refactor/fields/from-to-date";
import NotFound from "components/Texts/NotFound";
import UtilsFncs from "utils/UtilsFncs";

import Color from "ac-colors";

const useStyles = makeStyles({
  toggle: {
    textTransform: "capitalize",
  },
  selected: {
    fontWeight: "bold",
  },
});

const today = new Date();
const data7dias = new Date(today.setDate(today.getDate() - 8))
  .toISOString()
  .slice(0, 10);

const yesterday = new Date(today.setDate(new Date().getDate() - 1))
  .toISOString()
  .slice(0, 10);

const getHorizontalLabels = (stores) => {
  const labels = stores[0].DatasTaxaConversao?.map((store) => store.Data);

  return labels;
};

const generateRandomHexaColors = (length) => {
  const colors = ["#994E53"];

  do {
    const newColor = Color.randomFromString(colors[colors.length - 1])._hex;

    if (!colors.includes(newColor)) {
      colors.push(newColor);
    }
  } while (colors.length < length - 1);

  return colors;
};

const getEvolucaoDatasets = (stores) => {
  const graphColors = generateRandomHexaColors(stores.length);

  const datasets = stores?.map((store, index) => ({
    label: store.NomeLoja,
    data: store.DatasTaxaConversao?.map((data) => data.TaxaConversao),
    borderColor: graphColors[index],
    backgroundColor: graphColors[index],
    fill: false,
  }));

  return datasets;
};

export default function EvolutionStores({
  stores,
  pesquisar,
  searching,
  lastStoresParams,
  isMobile,
}) {
  const classes = useStyles();
  const [periodo, setPeriodo] = useState(
    lastStoresParams?.dtIni || lastStoresParams?.dtFim ? "periodo" : "ano"
  );
  const [dimensao, setDimensao] = useState(
    lastStoresParams?.dimensao || "diario"
  );
  const [dataInicial, setDataInicial] = React.useState(
    lastStoresParams?.dtIni
      ? UtilsFncs.ConvertDateFormat(lastStoresParams.dtIni, "-", "/")
      : UtilsFncs.GetMoment(data7dias).format("DD/MM/YYYY")
  );
  const [dataFinal, setDataFinal] = React.useState(
    lastStoresParams?.dtFim
      ? UtilsFncs.ConvertDateFormat(lastStoresParams.dtFim, "-", "/")
      : UtilsFncs.GetMoment(yesterday).format("DD/MM/YYYY")
  );

  const handlePeriodo = useCallback(
    (event, novoPeriodo) => {
      if (novoPeriodo === "ano" || novoPeriodo !== periodo) {
        setDataInicial(UtilsFncs.GetMoment(data7dias).format("DD/MM/YYYY"));
        setDataFinal(UtilsFncs.GetMoment(yesterday).format("DD/MM/YYYY"));
      }

      setPeriodo(novoPeriodo);
    },
    [periodo]
  );

  const handledimensao = useCallback((event, novaDimensao) => {
    setDimensao(novaDimensao);
  }, []);

  const handleConsulta = useCallback(async () => {
    const isAnnual = periodo === "ano";

    const params = {
      isAnnual,
      dtIni: isAnnual
        ? `${today.getFullYear()}-01-01`
        : UtilsFncs.ConvertDateFormat(dataInicial, "/", "-"),
      dtFim: isAnnual
        ? UtilsFncs.TodayDate()
        : UtilsFncs.ConvertDateFormat(dataFinal, "/", "-"),
      dimensao,
    };

    await pesquisar(params, true);
  }, [dataFinal, dataInicial, dimensao, periodo, pesquisar]);

  return (
    <>
      <Card>
        <CardHeader
          subheader={
            <Grid
              container
              component={Box}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs="auto">
                <Box
                  component={Typography}
                  variant="h2"
                  marginBottom="0!important"
                >
                  <Box component="span">Evolução Lojas - Taxa de Conversão</Box>
                </Box>
              </Grid>
            </Grid>
          }
        />
        <CardContent>
          <Grid container>
            <Grid
              item
              xs={isMobile ? 12 : 6}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <FormGroup>
                <ToggleButtonGroup
                  value={periodo}
                  exclusive
                  onChange={handlePeriodo}
                  size="small"
                  aria-label="filtro de periodo de Evoluçao Lojas"
                >
                  <ToggleButton
                    value="ano"
                    aria-label="periodo ano"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Ano Atual
                  </ToggleButton>
                  <ToggleButton
                    value="periodo"
                    aria-label="periodo customizavel"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Período
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormGroup>
              {periodo === "periodo" && (
                <FromToDateField
                  label="Período"
                  disabled={searching}
                  initialDate={dataInicial}
                  finalDate={dataFinal}
                  setInitialDate={setDataInicial}
                  setFinalDate={setDataFinal}
                  isMobile={isMobile}
                />
              )}
            </Grid>
            <Grid
              item
              xs={isMobile ? 12 : 6}
              style={{
                display: "flex",
                justifyContent: isMobile ? "flex-start" : "flex-end",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "flex-start",
              }}
            >
              <FormGroup>
                <ToggleButtonGroup
                  value={dimensao}
                  exclusive
                  onChange={handledimensao}
                  size="small"
                  aria-label="filtro de dimensão da evolução de lojas"
                >
                  <ToggleButton
                    value="mensal"
                    aria-label="dimensão mensal da evolução de lojas"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Mensal
                  </ToggleButton>
                  <ToggleButton
                    value="dia semana"
                    aria-label="dimensão de dia da semana da evolução de lojas"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Dia Semana
                  </ToggleButton>
                  <ToggleButton
                    value="diario"
                    aria-label="dimensão diária da evolução de lojas"
                    classes={{
                      selected: classes.selected,
                      root: classes.toggle,
                    }}
                  >
                    Diário
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormGroup>
              <Button
                variant="contained"
                onClick={handleConsulta}
                disabled={searching}
                startIcon={<SearchIcon style={{ width: 18, height: 18 }} />}
                style={{ display: "flex", marginLeft: isMobile ? "0" : "1rem" }}
              >
                {searching ? "Consultando..." : "Consultar"}
              </Button>
            </Grid>
            <Grid item xs={12} style={{ marginTop: isMobile ? "1rem" : 0 }}>
              {stores.length > 0 ? (
                <Box position="relative" height="350px">
                  <Line
                    data={{
                      labels: getHorizontalLabels(stores),
                      datasets: getEvolucaoDatasets(stores),
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                    }}
                  />
                </Box>
              ) : (
                <NotFound title="Não foram encontrados dados para Evolução Lojas - Taxa de Conversão." />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}
